import { useState } from "react";
import { Button, Form, Input, Modal, Table } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../components/PageHeader";
import { useApi } from "../../helpers/useApi";
import { useItemCategoriesQuery } from "../../helpers/queries";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { ItemCategory } from "../../api-models";

// Categories is a small reference list — /api/item-categories returns a plain
// array, not the Paginated<T> envelope the generic ListPage expects, so this page
// talks to the TanStack Query hook directly (same pattern as StockLocationsPage).
export default function CategoriesPage() {
  const { user } = useUser();
  const canWrite = hasPermission(user, "Items.Write");
  const { data: categories = [], isLoading } = useItemCategoriesQuery();
  const { request } = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ItemCategory | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  function reload() {
    queryClient.invalidateQueries({ queryKey: ["item-categories"] });
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    if (editing) await request({ method: "PUT", url: `/item-categories/${editing.id}`, data: values });
    else await request({ method: "POST", url: "/item-categories", data: values });
    setOpen(false);
    reload();
  }

  return (
    <>
      <PageHeader
        title="Item Categories"
        extra={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                form.resetFields();
                setOpen(true);
              }}
            >
              New category
            </Button>
          )
        }
      />
      <Table<ItemCategory>
        rowKey="id"
        loading={isLoading}
        dataSource={categories}
        columns={[
          { title: "Name", dataIndex: "name" },
          ...(canWrite
            ? [
                {
                  title: "",
                  key: "edit",
                  render: (_: unknown, r: ItemCategory) => (
                    <Button
                      type="link"
                      onClick={() => {
                        setEditing(r);
                        form.setFieldsValue(r);
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal title={editing ? "Edit category" : "New category"} open={open} onCancel={() => setOpen(false)} onOk={handleSubmit} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
